<template>
  <div class="sku-card-wrapper">
    <!-- Because of SEO and accessibility we need this link to cover the whole card
          and with an empty space. -->
    <NuxtLink
      class="stretched-link float-left text-decoration-none"
      :to="`${deal.get_absolute_url}`"
    >
      &nbsp;
    </NuxtLink>
    <DCard
      :key="deal.id"
      class="sku-card"
      border-variant="sundog"
      text-variant="dark"
    >
      <!-- Badges, Banners, Wishlist Buttons OR anything over the deals image -->
      <template #header>
        <div class="deal-image-container">
          <!-- use lazy image in header -->
          <!-- height equals 287px which is double the height of the image container -->
          <v-lazy-image
            class="deal-image"
            :class="showBanner && 'image-banner-offset'"
            :src="`${imgSrc}?width=450&aspect_ratio=16:9`"
            :src-placeholder="lazyPlaceholderImage"
            :alt="deal.public_name"
            height="144"
            width="255"
          />
        </div>
        <!-- Overlay for icons/badges at top of image -->
        <div v-if="showBadge" class="top-overlay">
          <SumoDayBadge
            v-if="isSumoDay(deal)"
            class="campaign-badge sumo-day-badge"
          />
          <BlackFridayBadge
            v-if="isBlackFriday(deal) || isBlackFridayLastCall(deal)"
            class="campaign-badge black-friday-badge"
          />
        </div>
        <WishlistsButton
          v-if="isWishlistCollection && !isSaveForLaterList"
          class="wishlists-button"
          :deal-id="deal.id"
          :for-collection="true"
          @click.stop
        />
        <!-- Delete Button for Saved for Later Collection  -->
        <DeleteButton
          v-if="isSaveForLaterList"
          class="wishlists-button"
          :deal-id="deal.id"
          @click.stop
        />
        <!-- Overlay for the banner/other callouts on the bottom of the image -->
        <div v-if="showBanner" class="bottom-overlay">
          <DealBanner
            class="product-card-deal-banner"
            :deal="deal"
            :is-black-friday-last-call="isBlackFridayLastCall(deal)"
            :is-last-call="isLastCall(deal)"
            :is-limited-time-sale="isLimitedTimeSale(deal)"
            :is-new-deal="isNewDeal(deal)"
          />
        </div>
      </template>

      <!-- Deal info -->
      <template #body>
        <div class="d-flex flex-column align-items-md-center">
          <div
            v-if="isSelect"
            class="badge badge-listing-type badge-select d-md-inline-flex
              order-1 order-md-0 mt-2 mt-md-0"
          >
            <SelectSVG
              width="128"
              height="11"
            />
          </div>

          <div
            v-if="isPlusExclusive"
            class="badge badge-listing-type badge-plus-exclusive d-md-inline-flex
              order-1 order-md-0 mt-2 mt-md-0"
          >
            <PlusExclusiveLogo
              height="11"
            />
          </div>
          <div
            class="deal-name-container text-truncate d-block mt-2 mt-md-0"
          >
            <strong :class="categoryName && 'deal-name text-truncate'">
              {{ deal.public_name }}
            </strong>

            <div v-if="categoryName">
              <span class="deal-category-in">in</span>
              <NuxtLink
                class="deal-category-link text-truncate"
                :to="categoryLink"
              >
                {{ categoryName }}
              </NuxtLink>
            </div>
          </div>

          <div class="deal-description mt-2">
            {{ cardDescription }}
          </div>

          <div class="d-md-none">
            <!-- mobile only reviews -->
            <DownloadsAndReviews
              :deal="deal"
              class="d-flex align-items-center mt-1"
            />

            <div v-if="showPrice" class="mt-2">
              <!-- Both elements in the same line to avoid an extra space between them -->
              <!-- eslint-disable-next-line max-len -->
              <span class="pricing-text">{{ pricingText }}</span><span v-if="showPlanType" class="plan-text">/{{ planType }}</span>
              <span
                v-if="deal.original_price"
                class="plan-original-price"
              >
                ${{ deal.original_price }}
              </span>
            </div>
          </div>
        </div>
      </template>

      <!-- Desktop only -->
      <template #footer>
        <!-- Reviews and downloads -->
        <DownloadsAndReviews class="download-reviews d-flex justify-content-center" :deal="deal" />

        <!-- Button for md up, just price for sm down -->
        <div v-if="showPrice" class="text-center mt-2">
          <!-- Both elements in the same line to avoid an extra space between them -->
          <!-- eslint-disable-next-line max-len -->
          <span class="pricing-text">{{ pricingText }}</span><span v-if="showPlanType" class="plan-text">/{{ planType }}</span>
          <span
            v-if="deal.original_price"
            class="plan-original-price"
          >
            {{ deal.original_price | currencyWithOptionalDecimal }}
          </span>
        </div>
      </template>
    </DCard>
  </div>
</template>
<script>
import { DCard } from '@appsumo/dorado-card';
import VLazyImage from 'v-lazy-image';

// constants
import SelectSVG from '~/static/appsumo-select.svg?inline';
import SumoDayBadge from '~/static/sumoday-badge.svg?inline';
import BlackFridayBadge from '~/static/campaigns/black-friday-badge-bg.svg?inline';
import PlusExclusiveLogo from '~/static/appsumo-plus-exclusive.svg?inline';
import { isWishlistCollectionSlug } from '~/util/global';

// components
import CampaignsMixin from '~/mixins/campaigns';
import ESDealDataMixin from '~/mixins/es-deal-data';
import DealBanner from '../utilities/DealBanner.vue';
import WishlistsButton from '../wishlists/Button.vue';
import DeleteButton from '../wishlists/DeleteButton.vue';
import DownloadsAndReviews from './DownloadsAndReviews.vue';

export default {
  components: {
    BlackFridayBadge,
    DCard,
    DealBanner,
    DeleteButton,
    DownloadsAndReviews,
    SelectSVG,
    SumoDayBadge,
    VLazyImage,
    WishlistsButton,
    PlusExclusiveLogo,
  },
  mixins: [CampaignsMixin, ESDealDataMixin('deal')],
  props: {
    deal: {
      type: Object,
      required: false,
      default: () => ({}),
    },
    title: {
      type: String,
      default: '',
    },
  },

  computed: {
    cardDescription() {
      return this.deal?.card_description;
    },
    imgSrc() {
      return this.deal?.media_url
        || 'https://appsumo2-cdn.appsumo.com/media/deals/images/as-web-Appily_App_Builder.png?width=400';
    },
    isWishlistCollection() {
      return isWishlistCollectionSlug(this.$route?.params.pathMatch);
    },
    showPrice() {
      const hasValidPrice = typeof this.deal?.default_plan?.price === 'number';
      // Is the deal in last call or not(coming soon or ended), and has a valid price
      // Need double check for last call, as coming soon can come through as tuple or boolean
      // boolean will eval as is, but tuple will return `true`, so we need to ensure it's actually
      // in LC to show the price.
      return (
        !(this.isComingSoonBuyState || this.isEndedDeal) || this.isLastCallBuyState
      ) && hasValidPrice;
    },
    wishlistData() {
      return this.$store.getters['wishlists/wishlist'];
    },
    showBadge() {
      return this.isSumoDay(this.deal)
        || this.isBlackFriday(this.deal)
        || this.isBlackFridayLastCall(this.deal);
    },
    showBanner() {
      return this.hasBanner(this.deal);
    },
    isSaveForLaterList() {
      return !!this.wishlistData?.is_save_for_later;
    },
  },
};
</script>
<style lang="scss" scoped>
@import '~/styles/dorado/main';
@import '~/styles/utils/breakpoints';

$banner-height: 24px;

.badge.select-badge {
  background-color: #eef9f1;
  box-shadow: none;
  display: inline-flex;
  width: 137px;
  height: 20px;

  ::v-deep path {
    // non-dorado color in figma
    fill: #069a2f;
  }
}

.sku-card-wrapper .stretched-link {
  width: 100%;
  height: 100%;
  display: block;
  position: absolute;
  font-size: 0;

  &::after {
    z-index: 2;
  }
}
/* stylelint-disable-next-line selector-pseudo-class-no-unknown */
.sku-card.card {
  .icon-plus-container {
    > span,
    > svg {
      display: inline-block;
      height: 24px;
      vertical-align: middle;

      path {
        fill: $dark;
      }
    }
  }

  ::v-deep {
    border: 0;
    box-shadow: none;
    cursor: pointer;
    min-height: 230px;

    &,
    .card-body {
      font-size: 12px;
      padding: 0;
    }

    .card-body {
      text-align: left;
    }

    .card-header {
      padding: 0;
      position: relative;
    }

    .card-footer {
      border: 0;
      display: none;
      padding: 0 0.75rem 1rem;
    }

    .taco-rating {
      height: auto;
      top: 0;
      width: auto;

      &::before {
        display: none;
      }
    }

    // Move image up a little bit if banner is visible
    .image-banner-offset {
      padding-bottom: $banner-height;
    }

    // Banner overrides - Because we're using the old component
    .banner {
      border-radius: 0 0 4px 4px;
      line-height: 14px;
      font-size: 12px;

      /**
      * TODO: Update ready red color in dorado repo.
      *
      * Last call and ending soon uses the ready red color (based on the sku card mockup),
      * however this color has been changed recently and the dorado repo doesn't have
      * the color updated yet.
      *
      */
      &.last-call,
      &.is-ending,
      &.sold-out {
        background-color: var(--brick);
      }

      &.coming-soon {
        background-color: var(--bolt);
      }

      &.limited-time-sale {
        background-color: var(--dark);
      }
    }

    .badge-listing-type {
      box-shadow: none;
      display: inline-flex;
      max-width: 200px;
      height: 20px;
      width: fit-content;
    }

    .badge-select {
      background-color: #eef9f1;

      path {
        // non-dorado color in figma
        fill: #069a2f;
      }
    }

    .badge-plus-exclusive {
      background-color: #feefec;

      svg {
        width: 100%;
      }

      path {
        // non-dorado color in figma
        fill: #ff5732;
      }
    }
  }

  .deal-category-link,
  .deal-category-in,
  .deal-name {
    display: inline-block;
    // Needed to add ellipsis to texts
    max-width: 90%;
    vertical-align: middle;
  }

  .deal-category-link {
    color: $dark;
    text-decoration: underline;
    position: relative;
    z-index: 2;

    &:hover {
      color: $dark;
    }
  }

  .deal-image-container {
    border-radius: 4px;
    min-height: 83px;
    overflow: hidden;
  }

  .deal-image {
    aspect-ratio: 16/9;
    height: 100%;
    object-fit: cover;
    width: 100%;
  }

  .deal-name-container {
    display: inline-block;
    vertical-align: middle;
    width: 100%;
  }

  .deal-description {
    /* stylelint-disable-next-line */
    -webkit-box-orient: vertical;
    /* stylelint-disable-next-line */
    display: -webkit-box;
    overflow: hidden;
    -webkit-line-clamp: 4;
  }

  .pricing-text,
  .plan-text {
    // Roboto
    font-family: Roboto, sans-serif;
    font-size: 16px;
    font-weight: 500;
  }

  .plan-text {
    font-size: 14px;
    font-weight: 400;
  }

  .plan-original-price {
    color: $grace;
    font-size: 12px;
    margin-left: 4px;
    text-decoration: line-through;
  }

  .bottom-overlay {
    // Banner height
    bottom: $banner-height;
    position: absolute;
    width: 100%;
  }

  .top-overlay {
    position: absolute;
    top: 0;
    justify-content: space-between;
    width: 100%;
    display: flex;
    left: -1px;
  }

  .wishlists-button {
    padding: 10px;
    position: absolute;
    right: 0;
    top: 0;
  }

  .download-reviews,
  .add-to-cart-button {
    position: relative;

    // Make add to cart button clickable
    z-index: 2;
  }

  .add-to-cart-button {
    font-size: 1rem;
    margin: 8px auto 0;
    max-width: 100%;
    padding: 0.25rem 1rem;
  }
}

@include media-breakpoint-down(md) {
  .sku-card.card ::v-deep .taco-rating {
    --taco-rating-size: 12px;
    --taco-rating-spacing: 2px;
  }
}

@include media-breakpoint-up(md) {
  .sku-card.card {
    ::v-deep {
      // Override legacy styles
      border: 1px solid #dadde5;
      box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
      // End override legacy styles

      height: 100%;

      &,
      .card-body {
        font-size: 16px;
      }

      .banner {
        border-radius: 0;
        font-size: 14px;
      }

      .card-body {
        text-align: center;
        padding: 1.15rem 0.75rem;
      }

      .card-footer {
        display: block;
      }

      .review-text {
        font-size: 14px;
      }
    }

    .deal-image-container {
      border-radius: 0;
      overflow: hidden;
    }

    .pricing-text {
      font-size: 1.5rem;
    }

    .plan-text {
      font-size: 1rem;
    }

    .plan-original-price,
    .download-reviews {
      font-size: 14px;
    }
  }
}

@include media-breakpoint-down(sm) {
  .campaign-badge.black-friday-badge,
  .campaign-badge.sumo-day-badge {
    width: 70px;
    height: auto;
  }
}
</style>
